import { useState, type FormEvent } from "react";
import { Search } from "lucide-react";
import { Button, Input, Select } from "../../ui";
import { PageWidth } from "../StoreShell";

export interface HomeSearchBarProps {
  /** Product types to search within; empty while they load. */
  departments: { id: number; name: string }[];
  /** This visitor's last few searches, newest first. */
  recent: string[];
  onSearch: (query: string, productTypeId?: number) => void;
}

/** The green search band across the home page (desktop kit SearchBar): a department, the words, and recent searches. */
export function HomeSearchBar({ departments, recent, onSearch }: HomeSearchBarProps) {
  const [query, setQuery] = useState("");
  const [department, setDepartment] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (query.trim()) onSearch(query.trim(), department ? Number(department) : undefined);
  };
  return (
    <div className="bg-green-700">
      <PageWidth className="flex flex-col gap-3 py-4 lg:flex-row lg:items-center lg:gap-4">
        <form role="search" onSubmit={submit} className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row">
          <Select
            aria-label="Department"
            value={department}
            onChange={(event) => setDepartment(event.target.value)}
            options={[{ value: "", label: "All departments" }, ...departments.map((type) => ({ value: String(type.id), label: type.name }))]}
            className="sm:w-[210px] sm:flex-none"
          />
          <span className="flex min-w-0 flex-1 gap-3">
            <Input
              aria-label="Search by part number or name"
              type="search"
              icon={Search}
              mono
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Part number or name"
              autoComplete="off"
              enterKeyHint="search"
              className="min-w-0 flex-1"
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </span>
        </form>
        {recent.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="type-small text-green-100">Recent:</span>
            {recent.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => onSearch(term)}
                className="inline-flex min-h-9 max-md:min-h-11 max-w-[180px] cursor-pointer items-center rounded-full border border-white/40 bg-surface-card px-3 type-sku [font-size:var(--text-xs)] text-text-strong hover:border-white"
              >
                <span className="truncate">{term}</span>
              </button>
            ))}
          </div>
        )}
      </PageWidth>
    </div>
  );
}
